import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { HABIT_TEMPLATES } from '../lib/templates';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, Check, Loader2, Sparkles } from 'lucide-react';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

// 6 curated starters — small enough to feel deliberate, broad enough to cover
// most lead-magnet visitors. User picks exactly 3.
const STARTER_PICKS = HABIT_TEMPLATES.filter((t) =>
  ['Drink Water', 'Meditate', 'Walk 10k Steps', 'Read', 'Write Gratitude', 'No Phone Before Bed'].includes(t.name),
);

const TOTAL_STEPS = 3;
const REQUIRED_PICKS = 3;

export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromCf = params.get('from') === 'cf';

  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  const displayName = useMemo(() => {
    const meta = user?.user_metadata || {};
    const raw = (meta.display_name || meta.name || '').trim();
    if (raw) return raw.split(' ')[0];
    const email = user?.email || '';
    return email ? email.split('@')[0] : 'friend';
  }, [user]);

  // Guard: if user already has habits, send them straight to /today (they're not new).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('habits')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (!cancelled && count && count > 0 && step === 1) {
        // Already onboarded — skip the wizard quietly.
        navigate('/today', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [user, navigate, step]);

  const togglePick = (name) => {
    setPicked((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= REQUIRED_PICKS) return prev;
      return [...prev, name];
    });
  };

  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    if (!user) return;
    if (picked.length !== REQUIRED_PICKS) {
      toast.error(`Pick ${REQUIRED_PICKS} habits to begin`);
      return;
    }
    setSaving(true);
    const rows = picked.map((name) => {
      const tpl = STARTER_PICKS.find((t) => t.name === name);
      return {
        user_id: user.id,
        name: tpl.name,
        description: tpl.description,
        type: tpl.type,
        target_value: tpl.target_value,
        unit: tpl.unit,
        color: tpl.color,
        icon: tpl.icon,
        time_of_day: tpl.time_of_day,
        schedule_type: tpl.schedule_type,
        schedule_days: tpl.schedule_days || [],
      };
    });
    const { error } = await supabase.from('habits').insert(rows);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Your flow is set. Welcome in.');
    navigate('/today', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="welcome-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          <div className="text-primary mb-8 flex justify-center"><Logo /></div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="welcome-progress">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i + 1 <= step ? 'bg-primary w-10' : 'bg-border w-5'
                }`}
              />
            ))}
          </div>

          <div className="rounded-2xl border border-border bg-card p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  data-testid="welcome-step-1"
                >
                  {fromCf && (
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs tracking-wide mb-5" data-testid="welcome-cf-badge">
                      <Sparkles className="h-3 w-3" strokeWidth={2} />
                      <span>Your habit OS is ready</span>
                    </div>
                  )}
                  <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                    Welcome, {displayName}.
                  </h1>
                  <p className="text-muted-foreground mt-3 leading-relaxed">
                    My Flow Habit is calm by design. No streaks shouting at you, no leaderboards.
                    Just three things you want to become — checked off, day after day.
                  </p>
                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { icon: 'target', title: 'Pick 3', body: 'Small wins compound. Three is enough.' },
                      { icon: 'sun', title: 'Show up', body: 'Skip days are fine. Forward is forward.' },
                      { icon: 'sparkles', title: 'See the flow', body: 'Watch your year fill in, square by square.' },
                    ].map((card) => {
                      const I = LucideIcons[toIconKey(card.icon)] || LucideIcons.Sparkles;
                      return (
                        <div key={card.title} className="rounded-xl border border-border bg-background/40 p-4">
                          <I className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          <div className="font-heading font-medium mt-3 text-sm">{card.title}</div>
                          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{card.body}</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end mt-8">
                    <Button onClick={next} className="rounded-full" data-testid="welcome-next-1">
                      Let&apos;s pick three <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  data-testid="welcome-step-2"
                >
                  <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Step 2 of 3</p>
                  <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">
                    Pick the three you&apos;ll show up for.
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Choose <span className="text-foreground font-medium">{REQUIRED_PICKS}</span> to start. You can edit, add, or remove anytime.
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {STARTER_PICKS.map((tpl) => {
                      const I = LucideIcons[toIconKey(tpl.icon)] || LucideIcons.Sparkles;
                      const isPicked = picked.includes(tpl.name);
                      const disabled = !isPicked && picked.length >= REQUIRED_PICKS;
                      return (
                        <button
                          type="button"
                          key={tpl.name}
                          onClick={() => togglePick(tpl.name)}
                          disabled={disabled}
                          data-testid={`welcome-pick-${tpl.name}`}
                          aria-pressed={isPicked}
                          className={`text-left rounded-2xl border p-4 transition-all duration-200 ${
                            isPicked
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                              : disabled
                              ? 'border-border bg-card/20 opacity-50 cursor-not-allowed'
                              : 'border-border bg-card/30 hover:-translate-y-0.5 hover:border-primary/40'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                              style={{ backgroundColor: `${tpl.color}1F`, color: tpl.color }}
                            >
                              <I className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-heading font-medium text-sm">{tpl.name}</div>
                                {isPicked && (
                                  <div className="h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                                    <Check className="h-3 w-3" strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{tpl.description}</p>
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                                {tpl.type === 'binary' ? 'Daily check' : tpl.type === 'time' ? `${tpl.target_value} min` : `${tpl.target_value} ${tpl.unit}`}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between mt-8">
                    <Button variant="ghost" onClick={back} className="rounded-full" data-testid="welcome-back-2">Back</Button>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground" data-testid="welcome-pick-count">
                        {picked.length} / {REQUIRED_PICKS} selected
                      </span>
                      <Button
                        onClick={next}
                        disabled={picked.length !== REQUIRED_PICKS}
                        className="rounded-full"
                        data-testid="welcome-next-2"
                      >
                        Continue <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  data-testid="welcome-step-3"
                >
                  <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Step 3 of 3</p>
                  <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">
                    Your flow, {displayName}.
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    A promise to yourself for tomorrow morning. We&apos;ll have today&apos;s check-in waiting.
                  </p>

                  <div className="mt-6 space-y-2">
                    {picked.map((name) => {
                      const tpl = STARTER_PICKS.find((t) => t.name === name);
                      const I = LucideIcons[toIconKey(tpl.icon)] || LucideIcons.Sparkles;
                      return (
                        <div
                          key={name}
                          className="flex items-center gap-3 rounded-xl border border-border bg-background/40 p-3.5"
                          data-testid={`welcome-summary-${name}`}
                        >
                          <div
                            className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${tpl.color}1F`, color: tpl.color }}
                          >
                            <I className="h-4 w-4" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading text-sm">{tpl.name}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {tpl.time_of_day} · {tpl.type === 'binary' ? 'daily check' : tpl.type === 'time' ? `${tpl.target_value} min` : `${tpl.target_value} ${tpl.unit}`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                    These are starters. You can edit times, swap habits, or add new ones once you&apos;re in.
                  </p>

                  <div className="flex items-center justify-between mt-8">
                    <Button variant="ghost" onClick={back} className="rounded-full" data-testid="welcome-back-3">Back</Button>
                    <Button
                      onClick={finish}
                      disabled={saving}
                      className="rounded-full"
                      data-testid="welcome-finish"
                    >
                      {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      Begin my flow
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="text-center mt-5">
            <button
              type="button"
              onClick={() => navigate('/today', { replace: true })}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="welcome-skip"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
